import { useEffect, useRef, useState, type ReactNode } from 'react';
import { wants3D } from '../gate-3d';
import type { TeamScene } from './scene';

/**
 * The Team section's globe. Same contract as `Hero3D`: `children` (a still
 * of the globe, the prerendered and no-WebGL version) renders first and never
 * leaves the DOM; the 3D mounts over it after load + idle, in the same box, so
 * nothing shifts, and the still fades out after the first 3D frame.
 *
 * Scroll turns the globe into place and draws the links; off screen it takes
 * no input, so it draws nothing.
 */
export function Team3D({ children }: { children: ReactNode }) {
  const host = useRef<HTMLDivElement | null>(null);
  const stage = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!wants3D()) return;
    let scene: TeamScene | null = null;
    let cancelled = false;
    const cleanups: Array<() => void> = [];

    const start = async () => {
      const { createTeamScene } = await import('./scene');
      if (cancelled || !stage.current || !host.current) return;
      scene = createTeamScene(stage.current, () => setReady(true));

      let onScreen = true;
      // 0 with the globe's top at the bottom of the screen, 1 once its middle
      // reaches the middle of the screen.
      const onScroll = () => {
        if (!onScreen) return;
        const r = host.current!.getBoundingClientRect();
        const vh = window.innerHeight;
        scene?.setProgress((vh - r.top) / (vh * 0.5 + r.height * 0.5));
      };
      const onPointer = (e: PointerEvent) => {
        if (e.pointerType !== 'mouse') return;
        const r = host.current!.getBoundingClientRect();
        scene?.setPointer(((e.clientX - r.left) / r.width) * 2 - 1, ((e.clientY - r.top) / r.height) * 2 - 1);
      };
      const io = new IntersectionObserver(([e]) => {
        onScreen = e.isIntersecting;
        if (onScreen) onScroll();
      });
      io.observe(host.current);
      const h = host.current;
      window.addEventListener('scroll', onScroll, { passive: true });
      h.addEventListener('pointermove', onPointer, { passive: true });
      cleanups.push(() => {
        io.disconnect();
        window.removeEventListener('scroll', onScroll);
        h.removeEventListener('pointermove', onPointer);
      });
      onScroll();
    };

    const kickoff = () => {
      const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
      if (idle) idle(() => start().catch(() => {}));
      else window.setTimeout(() => start().catch(() => {}), 200);
    };
    if (document.readyState === 'complete') kickoff();
    else {
      window.addEventListener('load', kickoff, { once: true });
      cleanups.push(() => window.removeEventListener('load', kickoff));
    }

    return () => {
      cancelled = true;
      cleanups.forEach((f) => f());
      scene?.dispose();
    };
  }, []);

  return (
    <div ref={host} className="t3d" data-ready={ready || undefined}>
      <div className="t3d-fallback">{children}</div>
      <div ref={stage} className="t3d-stage" aria-hidden />
    </div>
  );
}
